import { Link } from "@inertiajs/react"
import { Globe01 } from "@untitledui/icons"

import AppLogoIcon from "@/components/app-logo-icon"
import {
  Facebook,
  Instagram,
  TikTok,
  YouTube,
} from "@/components/ui/social-icons"
import { aboutPath, collabsPath, playlistsPath } from "@/routes"

interface FooterLink {
  label: string
  href: string
  internal?: boolean
}

interface FooterColumn {
  title: string
  links: FooterLink[]
}

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: "Explore",
    links: [
      { label: "Featured Playlists", href: playlistsPath(), internal: true },
      { label: "Community Collabs", href: collabsPath(), internal: true },
      { label: "Browse by Topic", href: "#" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Create a Playlist", href: "#" },
      { label: "Guidelines", href: "#" },
      { label: "Support", href: "#" },
    ],
  },
  {
    title: "About",
    links: [
      { label: "Our Story", href: aboutPath(), internal: true },
      { label: "Contact", href: "#" },
      { label: "Privacy", href: "#" },
    ],
  },
]

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/batyraus",
    icon: Instagram,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@batyraus",
    icon: TikTok,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/batyraus",
    icon: Facebook,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@batyraustralia",
    icon: YouTube,
  },
  {
    label: "Batyr website",
    href: "https://www.batyr.com.au",
    icon: Globe01,
  },
]

// The kit's brand-footer link idiom, carried on the white _on-brand opacity
// steps rather than text-footer-button-fg: the Batyr brand ramp makes
// brand-200 a chromatic straw, and nine straw links read as a yellow wash on
// navy, not a link lane. Focus outlines in the footer are white, not the
// Byline Blue ring — Byline Blue is ~2.17:1 on Masthead Navy and fails WCAG
// 1.4.11 (same call as video-player's on-dark controls).
const FOOTER_LINK_CLASS =
  "rounded-xs text-md font-semibold text-secondary_on-brand underline decoration-transparent underline-offset-2 outline-white transition duration-100 ease-linear hover:text-primary_on-brand hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-2"

export const PublicFooter = () => (
  <>
    {/* The edition's close-sign, one per page — owned by the layout via this
        component. Public pages must not render their own .end-mark. */}
    <div className="end-mark py-2 pb-10" aria-hidden="true" />
    <footer className="bg-brand-section py-12 md:pt-16">
      <div className="max-w-container mx-auto px-4 md:px-8">
        <div className="flex flex-col gap-12 lg:flex-row lg:gap-16">
          <div className="flex flex-col gap-6 lg:w-80 lg:shrink-0">
            <Link
              href="/"
              aria-label="Lists"
              className="inline-flex w-max items-center gap-2 rounded-xs whitespace-nowrap outline-white transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <AppLogoIcon
                aria-hidden="true"
                className="text-primary_on-brand h-8 w-auto"
              />
              <span className="text-primary_on-brand text-lg font-bold tracking-tight">
                Lists
              </span>
            </Link>
            <p className="text-md text-tertiary_on-brand max-w-sm">
              Curated mental health content with context from real people. Made
              with care for the herd.
            </p>
          </div>

          <nav aria-label="Footer" className="flex-1">
            <ul className="grid grid-cols-2 gap-8 md:grid-cols-3">
              {FOOTER_COLUMNS.map((column) => (
                <li key={column.title}>
                  {/* A UI label, not a heading — inside .app-canvas an h4
                      would render in the Aleo headline lane. */}
                  <p className="text-quaternary_on-brand text-sm font-semibold">
                    {column.title}
                  </p>
                  <ul className="mt-4 flex flex-col gap-3">
                    {column.links.map((link) => (
                      <li key={link.label}>
                        {link.internal ? (
                          <Link href={link.href} className={FOOTER_LINK_CLASS}>
                            {link.label}
                          </Link>
                        ) : (
                          <a href={link.href} className={FOOTER_LINK_CLASS}>
                            {link.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 flex flex-col-reverse justify-between gap-6 border-t border-white/15 pt-8 md:mt-16 md:flex-row md:items-center">
          <p className="text-md text-quaternary_on-brand">
            © {new Date().getFullYear()} OurHerd | Lists — a batyr initiative.
          </p>
          <ul className="flex gap-6">
            {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-icon-fg-brand_on-brand hover:text-primary_on-brand -m-2 flex rounded-xs p-2 outline-white transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <Icon aria-hidden="true" className="size-6" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  </>
)
